<script setup lang="ts" generic="T extends PartialMarkup">
import StyledInput from "@/components/StyledInput.vue";
import StyledSelect from "@/components/StyledSelect.vue";
import { percentageOptions } from "@/data/percentageOptions";
import { MarkupTypes } from "@/enums/markupTypes";
import type { Markup } from "@/interfaces/markup";
import { Switch } from "@headlessui/vue";
import { reactive, ref, watch } from "vue";

export interface PartialMarkup extends Partial<Markup> {
  [key: string]: any;
}

interface Props {
  modelValue: T;
  label?: string;
  placeholder?: string;
}

const props = withDefaults(defineProps<Props>(), {
  label: "",
  placeholder: "",
});

interface Emits {
  (event: "update:modelValue", value: PartialMarkup): void;
  (event: "focusIn", value: FocusEvent): void;
  (event: "focusOut", value: FocusEvent): void;
}

const emit = defineEmits<Emits>();

const markup = reactive<PartialMarkup>({ ...props.modelValue });
const typeSwitch = ref(props.modelValue.type === MarkupTypes.Amount);

watch(typeSwitch, (value) => {
  markup.type = value ? MarkupTypes.Amount : MarkupTypes.Percent;

  markup.value = 0;
});

watch(markup, (value) => {
  emit("update:modelValue", value);
});
</script>

<template>
  <div class="flex flex-col gap-[0.75rem]">
    <span class="font-medium text-base">{{ label }}</span>

    <fieldset class="flex gap-[2.25rem]">
      <div
        class="flex items-center justify-between gap-2 py-[0.625rem] px-[1.625rem] rounded-md w-1/2 border-2 h-[45px] text-sm">
        <span class="capitalize">{{
          markup.type == "percent" ? "Porcentaje" : "Cantidad"
          }}</span>

        <Switch v-model="typeSwitch" :class="typeSwitch ? 'bg-teal-900' : 'bg-teal-700'"
          class="relative inline-flex h-[20px] w-[42px] shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75">
          <span aria-hidden="true" :class="typeSwitch ? 'translate-x-[22px]' : 'translate-x-0'"
            class="pointer-events-none inline-block h-[16px] w-[16px] transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out" />
        </Switch>
      </div>

      <StyledInput v-if="typeSwitch" type="number" class="w-1/2 !h-[45px]" v-model="markup.value"
        placeholder="ej. 235.5" v-on:focus-in="(e) => emit('focusIn', e)" v-on:focus-out="(e) => emit('focusOut', e)" />

      <StyledSelect v-else class="w-1/2" v-model="markup.value" :placeholder="placeholder"
        v-on:focus-in="(e) => emit('focusIn', e)" v-on:focus-out="(e) => emit('focusOut', e)"
        :options="percentageOptions" />
    </fieldset>
  </div>
</template>

<style scoped></style>
